import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Save, Trash2, AlertTriangle, UserPlus } from 'lucide-react';

export function EditMemberDialog({ editMember, setEditMember, onSave }) {
  if (!editMember) return null;
  return (
    <Dialog open={!!editMember} onOpenChange={() => setEditMember(null)}>
      <DialogContent className="bg-[#1B1B1B] border-[#2A2A2E] text-white max-w-sm">
        <DialogHeader><DialogTitle className="text-white">Modifier {editMember.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-zinc-400 text-xs">Nom</Label>
            <Input value={editMember.name} onChange={e => setEditMember({...editMember, name: e.target.value})} className="bg-[#0F0F0F] border-[#2A2A2E] text-white" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-zinc-400 text-xs">Role</Label>
              <select value={editMember.role} onChange={e => setEditMember({...editMember, role: e.target.value})} className="w-full h-10 bg-[#0F0F0F] border border-[#2A2A2E] text-white rounded-sm px-3 text-sm">
                <option value="user">Membre</option>
                <option value="admin">Coach</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-xs">Points</Label>
              <Input type="number" value={editMember.points} onChange={e => setEditMember({...editMember, points: parseInt(e.target.value) || 0})} className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono" />
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setEditMember(null)} className="flex-1 border-zinc-700 text-zinc-400">Annuler</Button>
            <Button onClick={onSave} className="flex-1 bg-[#FF6B35] text-black hover:bg-[#E85A25]"><Save className="w-4 h-4 mr-1" /> Sauver</Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function EditMetricsDialog({ editMetrics, setEditMetrics, onSave }) {
  if (!editMetrics) return null;
  return (
    <Dialog open={!!editMetrics} onOpenChange={() => setEditMetrics(null)}>
      <DialogContent className="bg-[#1B1B1B] border-[#2A2A2E] text-white max-w-sm">
        <DialogHeader><DialogTitle className="text-white">Metriques — {editMetrics.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-zinc-400 text-xs">Date de la mesure</Label>
            <Input type="date" value={editMetrics.timestamp ? editMetrics.timestamp.substring(0, 10) : ''} onChange={e => setEditMetrics({...editMetrics, timestamp: e.target.value})} className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono" data-testid="metrics-date-input" />
            <p className="text-[10px] text-zinc-500">Laisser vide = date d'aujourd'hui</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-zinc-400 text-xs">Poids (kg)</Label>
              <Input type="number" step="0.1" value={editMetrics.weight} onChange={e => setEditMetrics({...editMetrics, weight: e.target.value})} className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono" data-testid="metrics-weight-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-xs">Masse graisseuse (%)</Label>
              <Input type="number" step="0.1" value={editMetrics.body_fat} onChange={e => setEditMetrics({...editMetrics, body_fat: e.target.value})} className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-xs">Masse musculaire (kg)</Label>
              <Input type="number" step="0.1" value={editMetrics.muscle_mass} onChange={e => setEditMetrics({...editMetrics, muscle_mass: e.target.value})} className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-xs">Age metabolique</Label>
              <Input type="number" value={editMetrics.metabolic_age} onChange={e => setEditMetrics({...editMetrics, metabolic_age: e.target.value})} className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono" />
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setEditMetrics(null)} className="flex-1 border-zinc-700 text-zinc-400">Annuler</Button>
            <Button onClick={onSave} className="flex-1 bg-blue-600 text-white hover:bg-blue-700"><Save className="w-4 h-4 mr-1" /> Enregistrer</Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function DeleteConfirmDialog({ deleteConfirm, setDeleteConfirm, onConfirm }) {
  if (!deleteConfirm) return null;
  return (
    <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
      <DialogContent className="bg-[#1B1B1B] border-[#2A2A2E] text-white max-w-sm">
        <DialogHeader><DialogTitle className="text-white flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-red-400" /> Supprimer ?</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-zinc-400 text-sm">
            Supprimer <strong className="text-white">{deleteConfirm.name}</strong> et toutes ses donnees (metriques, exercices, repas, presences) ?
          </p>
          <p className="text-xs text-red-400">Cette action est irreversible.</p>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)} className="flex-1 border-zinc-700 text-zinc-400">Annuler</Button>
            <Button onClick={() => onConfirm(deleteConfirm.id)} className="flex-1 bg-red-600 text-white hover:bg-red-700" data-testid="confirm-delete">
              <Trash2 className="w-4 h-4 mr-1" /> Supprimer
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function CreateMemberDialog({ showCreate, setShowCreate, newMember, setNewMember, onCreate }) {
  return (
    <Dialog open={showCreate} onOpenChange={() => setShowCreate(false)}>
      <DialogContent className="bg-[#1B1B1B] border-[#2A2A2E] text-white max-w-sm">
        <DialogHeader><DialogTitle className="text-white">Nouveau profil</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-zinc-400 text-xs">Nom / Pseudo</Label>
            <Input value={newMember.name} onChange={e => setNewMember({...newMember, name: e.target.value})} placeholder="Ex: Thomas" className="bg-[#0F0F0F] border-[#2A2A2E] text-white" data-testid="create-member-name" />
          </div>
          <div className="space-y-2">
            <Label className="text-zinc-400 text-xs">Role</Label>
            <select value={newMember.role} onChange={e => setNewMember({...newMember, role: e.target.value})} className="w-full h-10 bg-[#0F0F0F] border border-[#2A2A2E] text-white rounded-sm px-3 text-sm" data-testid="create-member-role">
              <option value="user">Membre</option>
              <option value="admin">Coach / Admin</option>
            </select>
            <p className="text-[10px] text-zinc-500">Mot de passe : {newMember.role === 'admin' ? 'Alpha' : 'lameute'}</p>
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowCreate(false)} className="flex-1 border-zinc-700 text-zinc-400">Annuler</Button>
            <Button onClick={onCreate} className="flex-1 bg-[#FF6B35] text-white hover:bg-[#FF8C55]" data-testid="create-member-submit">Creer</Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
